'use client';

import { cn } from '@/lib/utils';
import type { ConductAppearance } from '@/lib/conduct-presentation';

function FilledMoonIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M21.75 15.07A9.75 9.75 0 0 1 8.93 2.25a.75.75 0 0 0-.9-.9A11.25 11.25 0 1 0 22.65 15.97a.75.75 0 0 0-.9-.9Z" />
    </svg>
  );
}

function FilledSunIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <circle cx="12" cy="12" r="4.25" />
      <path d="M12 1.75a.75.75 0 0 1 .75.75v1.75a.75.75 0 0 1-1.5 0V2.5A.75.75 0 0 1 12 1.75Zm0 17.5a.75.75 0 0 1 .75.75V21.75a.75.75 0 0 1-1.5 0v-1.75a.75.75 0 0 1 .75-.75ZM22.25 12a.75.75 0 0 1-.75.75h-1.75a.75.75 0 0 1 0-1.5H21.5a.75.75 0 0 1 .75.75ZM4.25 12a.75.75 0 0 1-.75.75H1.75a.75.75 0 0 1 0-1.5H3.5A.75.75 0 0 1 4.25 12Zm15.01-7.26a.75.75 0 0 1 0 1.06l-1.24 1.24a.75.75 0 1 1-1.06-1.06l1.24-1.24a.75.75 0 0 1 1.06 0ZM7.04 16.96a.75.75 0 0 1 0 1.06l-1.24 1.24a.75.75 0 0 1-1.06-1.06l1.24-1.24a.75.75 0 0 1 1.06 0Zm11.22 1.06a.75.75 0 0 1-1.06 0l-1.24-1.24a.75.75 0 1 1 1.06-1.06l1.24 1.24a.75.75 0 0 1 0 1.06ZM7.04 7.04a.75.75 0 0 1-1.06 0L4.74 5.8A.75.75 0 0 1 5.8 4.74l1.24 1.24a.75.75 0 0 1 0 1.06Z" />
    </svg>
  );
}

type FairtradeAppearanceToggleProps = {
  appearance: ConductAppearance;
  onChange: (next: ConductAppearance) => void;
  className?: string;
};

export function FairtradeAppearanceToggle({
  appearance,
  onChange,
  className,
}: FairtradeAppearanceToggleProps) {
  return (
    <div
      className={cn(
        'inline-flex shrink-0 items-center rounded-full border border-sky-400/25 bg-white/[0.06] p-0.5 backdrop-blur-sm',
        className
      )}
      role="group"
      aria-label="Alternar aparência do conteúdo"
    >
      <button
        type="button"
        onClick={() => onChange('dark')}
        className={cn(
          'inline-flex h-8 w-8 items-center justify-center rounded-full transition-colors duration-200',
          appearance === 'dark'
            ? 'bg-sky-500/25 text-sky-100'
            : 'text-sky-300/75 hover:bg-sky-400/10 hover:text-sky-100'
        )}
        aria-pressed={appearance === 'dark'}
        aria-label="Night mode"
        title="Night mode"
      >
        <FilledMoonIcon className="h-3.5 w-3.5" />
      </button>
      <button
        type="button"
        onClick={() => onChange('light')}
        className={cn(
          'inline-flex h-8 w-8 items-center justify-center rounded-full transition-colors duration-200',
          appearance === 'light'
            ? 'bg-sky-500/25 text-sky-100'
            : 'text-sky-300/75 hover:bg-sky-400/10 hover:text-sky-100'
        )}
        aria-pressed={appearance === 'light'}
        aria-label="Modo claro"
        title="Modo claro"
      >
        <FilledSunIcon className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
